import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { colorsApi } from '../../services/settingsApi';
import { useTranslation } from '../../hooks/useTranslation';

export default function ColorsTab() {
    const [colors, setColors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filteredColors, setFilteredColors] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingColor, setEditingColor] = useState(null);
    const [formData, setFormData] = useState({
        color_name: '',
        color_code: '',
        hex_code: '#000000',
        is_active: true
    });
    const { getMessage, getCommonLabel } = useTranslation();

    useEffect(() => {
        fetchColors();
    }, []);

    useEffect(() => {
        let filtered = colors;

        // Filter by search query
        if (searchQuery.trim() !== '') {
            filtered = filtered.filter(color =>
                color.color_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                color.color_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                color.hex_code?.toLowerCase().includes(searchQuery.toLowerCase())
            );
        }

        setFilteredColors(filtered);
    }, [searchQuery, colors]);

    const fetchColors = async () => {
        try {
            setLoading(true);
            console.log('Fetching colors...');
            const response = await colorsApi.getAll();
            console.log('Colors API response:', response);

            // Try different response structures
            let colorsData = [];
            if (response.data) {
                if (Array.isArray(response.data)) {
                    colorsData = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    colorsData = response.data.data;
                } else if (response.data.colors && Array.isArray(response.data.colors)) {
                    colorsData = response.data.colors;
                }
            }

            console.log('Parsed colors data:', colorsData);
            setColors(colorsData);

        } catch (error) {
            console.error('Error fetching colors:', error);
            toast.error(`Failed to load colors: ${error.response?.data?.message || error.message}`);
            setColors([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleAddColor = () => {
        setEditingColor(null);
        setFormData({
            color_name: '',
            color_code: '',
            hex_code: '#000000',
            is_active: true
        });
        setShowModal(true);
    };

    const handleEditColor = (color) => {
        setEditingColor(color);
        setFormData({
            color_name: color.color_name || '',
            color_code: color.color_code || '',
            hex_code: color.hex_code || '#000000',
            is_active: color.is_active !== false
        });
        setShowModal(true);
    };

    const handleDeleteColor = async (color) => {
        if (!window.confirm(`Are you sure you want to delete color "${color.color_name}"?`)) {
            return;
        }

        try {
            await colorsApi.delete(color.color_id);
            toast.success('Color deleted successfully');
            fetchColors();
        } catch (error) {
            console.error('Error deleting color:', error);
            toast.error('Failed to delete color');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingColor) {
                await colorsApi.update(editingColor.color_id, formData);
                toast.success('Color updated successfully');
            } else {
                await colorsApi.create(formData);
                toast.success('Color created successfully');
            }

            setShowModal(false);
            fetchColors();
        } catch (error) {
            console.error('Error saving color:', error);
            toast.error(error.response?.data?.error || 'Failed to save color');
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingColor(null);
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        🎨 {getMessage('settings.colors') || 'Car Colors'}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {getMessage('settings.colors_desc') || 'Manage car colors for vehicle registration'}
                    </p>
                </div>
                <div className="flex space-x-3">
                    <button
                        onClick={fetchColors}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                    >
                        🔄 {getMessage('common.refresh') || 'Refresh'}
                    </button>
                    <button
                        onClick={handleAddColor}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors duration-200"
                    >
                        ➕ {getCommonLabel('add') || 'Add Color'}
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="relative flex-1 max-w-md">
                    <input
                        type="text"
                        placeholder={getMessage('common.search') || 'Search colors...'}
                        value={searchQuery}
                        onChange={handleSearch}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                        🔍
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {filteredColors.length} color(s)
                </div>
            </div>

            {/* Colors Table */}
            <div className="bg-white dark:bg-gray-700 rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <span className="ml-3 text-gray-600 dark:text-gray-400">Loading...</span>
                        </div>
                    ) : filteredColors.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="text-gray-400 dark:text-gray-500 text-lg mb-2">🎨</div>
                            <p className="text-gray-600 dark:text-gray-400">
                                {searchQuery ? 'No colors found matching your search' : 'No colors available'}
                            </p>
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-600">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Color
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Code
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Hex Code
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Preview
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white dark:bg-gray-700 divide-y divide-gray-200 dark:divide-gray-600">
                                {filteredColors.map((color) => (
                                    <tr key={color.color_id} className="hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors duration-150">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div
                                                    className="flex-shrink-0 h-10 w-10 rounded-full border-2 border-gray-300 dark:border-gray-500"
                                                    style={{ backgroundColor: color.hex_code || '#cccccc' }}
                                                ></div>
                                                <div className="ml-4">
                                                    <div className="text-sm font-bold text-gray-900 dark:text-white">
                                                        {color.color_name}
                                                    </div>
                                                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        ID: #{color.color_id}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white font-mono">
                                                {color.color_code || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 dark:text-white font-mono">
                                                {color.hex_code || '-'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div
                                                className="h-8 w-16 rounded border border-gray-300 dark:border-gray-500"
                                                style={{ backgroundColor: color.hex_code || '#cccccc' }}
                                                title={color.hex_code}
                                            ></div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color.is_active
                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                                                }`}>
                                                <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${color.is_active ? 'bg-green-500' : 'bg-red-500'
                                                    }`}></span>
                                                {color.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleEditColor(color)}
                                                className="text-yellow-600 hover:text-yellow-900 dark:text-yellow-400 dark:hover:text-yellow-300 mr-3"
                                                title="Edit"
                                            >
                                                ✏️ Edit
                                            </button>
                                            <button
                                                onClick={() => handleDeleteColor(color)}
                                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                                title="Delete"
                                            >
                                                🗑️ Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {editingColor ? 'Edit Color' : 'Add New Color'}
                            </h3>
                            <button
                                onClick={closeModal}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Color Name *
                                </label>
                                <input
                                    type="text"
                                    name="color_name"
                                    value={formData.color_name}
                                    onChange={handleInputChange}
                                    required
                                    placeholder="e.g., White, Black, Silver"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Color Code
                                </label>
                                <input
                                    type="text"
                                    name="color_code"
                                    value={formData.color_code}
                                    onChange={handleInputChange}
                                    placeholder="e.g., WHT, BLK, SLV"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Hex Code
                                </label>
                                <div className="flex space-x-3">
                                    <input
                                        type="color"
                                        name="hex_code"
                                        value={formData.hex_code}
                                        onChange={handleInputChange}
                                        className="h-10 w-16 border border-gray-300 dark:border-gray-600 rounded cursor-pointer"
                                    />
                                    <input
                                        type="text"
                                        name="hex_code"
                                        value={formData.hex_code}
                                        onChange={handleInputChange}
                                        placeholder="#FFFFFF"
                                        pattern="^#[0-9A-Fa-f]{6}$"
                                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    Click the color box to pick a color or enter hex code (e.g., #FF5733)
                                </p>
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                    Active
                                </label>
                            </div>

                            {/* Color Preview */}
                            <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Preview
                                </label>
                                <div className="flex items-center space-x-4">
                                    <div
                                        className="h-16 w-16 rounded-lg border-2 border-gray-300 dark:border-gray-500 shadow-inner"
                                        style={{ backgroundColor: formData.hex_code || '#cccccc' }}
                                    ></div>
                                    <div>
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                            {formData.color_name || 'Color Name'}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                            {formData.hex_code || '#000000'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="flex space-x-3 pt-4">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
                                >
                                    {editingColor ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
